import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

// Session timeout configuration (in milliseconds)
const SESSION_CONFIG = {
  admin: {
    timeout: 8 * 60 * 60 * 1000,      // 8 hours for admin
    warningBefore: 5 * 60 * 1000       // Show warning 5 minutes before
  },
  default: {
    timeout: 4 * 60 * 60 * 1000,      // 4 hours for other users
    warningBefore: 5 * 60 * 1000       // Show warning 5 minutes before
  }
};

export const useSessionManager = (user, onLogout) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const isAdmin = user?.roles?.includes('admin') || user?.role === 'admin';
  const config = isAdmin ? SESSION_CONFIG.admin : SESSION_CONFIG.default;

  // Cleanup all timers
  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    toast.info('Sessione scaduta per inattivit\u00e0. Effettua nuovamente il login.');
    if (onLogout) {
      setTimeout(() => onLogout(), 0);
    }
  }, [clearAllTimers, onLogout]);

  // Start countdown when warning shows
  const startCountdown = useCallback(() => {
    setShowWarning(true);
    setTimeRemaining(Math.floor(config.warningBefore / 1000));
    
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    countdownRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setTimeout(() => handleSessionExpired(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [config.warningBefore, handleSessionExpired]);

  // Setup session timers
  const setupTimers = useCallback(() => {
    clearAllTimers();
    lastActivityRef.current = Date.now();
    
    // Warning timer
    warningTimeoutRef.current = setTimeout(() => {
      startCountdown();
    }, config.timeout - config.warningBefore);
    
    // Logout timer
    timeoutRef.current = setTimeout(() => {
      handleSessionExpired();
    }, config.timeout);
  }, [clearAllTimers, config, startCountdown, handleSessionExpired]);

  // Renew session
  const renewSession = useCallback(() => {
    setShowWarning(false);
    setTimeRemaining(0);
    setupTimers();
    toast.success('Sessione rinnovata!');
  }, [setupTimers]);

  // Reset activity timer on user interaction (debounced)
  const resetActivityTimer = useCallback(() => {
    // If warning is showing, don't auto-reset - user must explicitly renew
    if (showWarning) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Debounce: only reset if more than 60 seconds since last reset
    if (timeSinceLastActivity > 60000) {
      lastActivityRef.current = now;
      setupTimers();
    }
  }, [showWarning, setupTimers]);

  // Initialize session manager
  useEffect(() => {
    if (!user) return;
    
    setupTimers();

    // Listen for ALL user activity events
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click', 'focus', 'visibilitychange'];
    
    const handleActivity = () => {
      // Only track activity if page is visible
      if (document.visibilityState !== 'hidden') {
        resetActivityTimer();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also reset on any API call (axios requests indicate active usage)
    const requestInterceptor = window.__axiosActivityCallback;
    if (requestInterceptor) {
      requestInterceptor.current = handleActivity;
    }

    return () => {
      clearAllTimers();
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [user?.id]); // eslint-disable-line

  // Manual logout
  const logout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    if (onLogout) {
      onLogout();
    }
  }, [clearAllTimers, onLogout]);

  return {
    showWarning,
    timeRemaining,
    renewSession,
    logout
  };
};

export default useSessionManager;
